/**
 * Dashboard quick-action modals — compact mandatory-only forms with section grouping,
 * availability badges, and "Advanced" escape links. Each modal handles its own state.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { flaskApi } from '@/lib/flaskApi'
import { useNotification, useNavigation } from '@refinedev/core'
import { AGENCY_TYPE_OPTIONS } from '@/constants/agencyRegistration'
import {
  Building2, Mail, MessageCircle, Loader2, CheckCircle2, XCircle,
  ExternalLink, CheckCircle, ArrowRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'

/* ─── Shared primitives ───────────────────────────────────────── */
type Status = 'idle' | 'submitting' | 'success' | 'error'
type AvailabilityStatus = 'idle' | 'checking' | 'available' | 'taken'

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 dark:text-slate-500">
      {children}
    </span>
  )
}

function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null
  return (
    <p className="flex items-center gap-1 text-[10px] text-destructive mt-0.5 font-medium">
      <XCircle size={9} className="shrink-0" /> {msg}
    </p>
  )
}

function AvailabilityBadge({ status }: { status: AvailabilityStatus }) {
  if (status === 'idle') return null
  if (status === 'checking') return (
    <span className="flex items-center gap-1 text-[10px] text-slate-400">
      <Loader2 size={9} className="animate-spin" /> checking…
    </span>
  )
  if (status === 'available') return (
    <span className="flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">
      <CheckCircle size={9} /> available
    </span>
  )
  return (
    <span className="flex items-center gap-1 text-[10px] text-destructive font-semibold">
      <XCircle size={9} /> taken
    </span>
  )
}

function ModalSuccess({
  icon, message, onReset, onClose, resetLabel = 'Create Another',
}: {
  icon: React.ReactNode
  message: string
  onReset: () => void
  onClose: () => void
  resetLabel?: string
}) {
  return (
    <div className="flex flex-col items-center gap-5 py-8 text-center">
      <div className="relative">
        <div className="w-16 h-16 rounded-2xl bg-emerald-50 dark:bg-emerald-900/25 flex items-center justify-center">
          {icon}
        </div>
        <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
          <CheckCircle2 size={13} className="text-white" />
        </div>
      </div>
      <div>
        <p className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-1">Done!</p>
        <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xs">{message}</p>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onReset} className="text-xs h-8">
          {resetLabel}
        </Button>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs h-8 px-4" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  )
}

function AdvancedLink({ path, label, onNavigate }: {
  path: string
  label: string
  onNavigate: (path: string) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onNavigate(path)}
      className="group flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500 hover:text-[#076698] dark:hover:text-sky-400 transition-colors"
    >
      <ExternalLink size={10} className="group-hover:scale-110 transition-transform" />
      {label}
    </button>
  )
}

function SectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <div className="h-px flex-1 bg-border/40 dark:bg-white/8" />
      <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-400 dark:text-slate-600 px-1">
        {label}
      </span>
      <div className="h-px flex-1 bg-border/40 dark:bg-white/8" />
    </div>
  )
}

function ErrorBanner({ msg }: { msg?: string }) {
  if (!msg) return null
  return (
    <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200/60 dark:border-red-900/50 text-xs text-red-700 dark:text-red-400">
      <XCircle size={13} className="shrink-0 mt-0.5" />
      <span>{msg}</span>
    </div>
  )
}

/* ─── Modal shell / header ────────────────────────────────────── */
function ModalShell({
  open, onClose, accentBg, accentText, icon, title, subtitle, children,
}: {
  open: boolean
  onClose: () => void
  accentBg: string
  accentText: string
  icon: React.ReactNode
  title: string
  subtitle: string
  children: React.ReactNode
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="!max-w-md p-0 overflow-hidden rounded-2xl border border-white/70 dark:border-white/10 bg-white/90 dark:bg-slate-800/90 backdrop-blur-2xl shadow-2xl">
        {/* Colored header strip */}
        <div className={cn('px-5 pt-5 pb-4', accentBg)}>
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/30 dark:bg-white/10 flex items-center justify-center shrink-0 shadow-sm">
                <span className={accentText}>{icon}</span>
              </div>
              <div>
                <DialogTitle className={cn('text-base font-extrabold', accentText)}>{title}</DialogTitle>
                <p className={cn('text-[11px] mt-0.5 opacity-80', accentText)}>{subtitle}</p>
              </div>
            </div>
          </DialogHeader>
        </div>
        {/* Body */}
        <div className="px-5 py-4">
          {children}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/* ─── Create Agency Quick Modal ───────────────────────────────── */
export function QuickCreateAgencyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [agencyName, setAgencyName] = useState('')
  const [type, setType] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [adminUsername, setAdminUsername] = useState('')
  const [nameAvail, setNameAvail] = useState<AvailabilityStatus>('idle')
  const [emailAvail, setEmailAvail] = useState<AvailabilityStatus>('idle')
  const [usernameAvail, setUsernameAvail] = useState<AvailabilityStatus>('idle')
  const [status, setStatus] = useState<Status>('idle')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const debounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const { open: notify } = useNotification()
  const { push } = useNavigation()

  const checkField = useCallback((field: string, value: string, setter: (s: AvailabilityStatus) => void) => {
    clearTimeout(debounceRef.current[field])
    if (!value || value.length < 2) { setter('idle'); return }
    setter('checking')
    debounceRef.current[field] = setTimeout(async () => {
      try {
        const res = await flaskApi.get<{ available: boolean }>(
          `/check-availability?field=${field}&value=${encodeURIComponent(value)}`
        )
        setter(res.data.available ? 'available' : 'taken')
      } catch { setter('idle') }
    }, 500)
  }, [])

  const reset = () => {
    setAgencyName(''); setType(''); setContactEmail(''); setPhone('')
    setAdminEmail(''); setAdminUsername('')
    setNameAvail('idle'); setEmailAvail('idle'); setUsernameAvail('idle')
    setStatus('idle'); setErrors({})
  }

  const handleClose = () => { reset(); onClose() }

  const validate = () => {
    const e: Record<string, string> = {}
    if (!agencyName.trim()) e.agencyName = 'Required'
    if (!type) e.type = 'Required'
    if (!contactEmail.trim()) e.contactEmail = 'Required'
    if (!phone.trim()) e.phone = 'Required'
    if (!adminEmail.trim()) e.adminEmail = 'Required'
    if (!adminUsername.trim()) e.adminUsername = 'Required'
    if (nameAvail === 'taken') e.agencyName = 'Name already taken'
    if (emailAvail === 'taken') e.contactEmail = 'Email in use'
    if (usernameAvail === 'taken') e.adminUsername = 'Username taken'
    return e
  }

  const handleSubmit = async () => {
    const e = validate()
    if (Object.keys(e).length > 0) { setErrors(e); return }
    setStatus('submitting')
    try {
      await flaskApi.post('/agency', {
        agency_name: agencyName, type, contact_email: contactEmail, phone,
        email: adminEmail, username: adminUsername,
        admin_user_mode: 'new',
        industry: '', company_size: '', contact_person: '',
        job_seeker_registration_method: 'email',
      })
      setStatus('success')
    } catch (err: unknown) {
      const desc = (err as { response?: { data?: { description?: string } } })?.response?.data?.description ?? 'Failed to create agency'
      notify?.({ type: 'error', message: 'Failed to create agency', description: desc })
      setStatus('error')
      setErrors({ _: desc })
    }
  }

  return (
    <ModalShell
      open={open} onClose={handleClose}
      accentBg="bg-gradient-to-r from-[#076698]/10 to-sky-50/60 dark:from-sky-900/25 dark:to-sky-900/10"
      accentText="text-[#076698] dark:text-sky-300"
      icon={<Building2 size={18} />}
      title="Quick Create Agency"
      subtitle="Mandatory fields only — advanced options on the full page"
    >
      {status === 'success' ? (
        <ModalSuccess
          icon={<Building2 size={22} className="text-emerald-600" />}
          message={`Agency "${agencyName}" was created. You can configure advanced settings from the full agency page.`}
          onReset={reset}
          onClose={handleClose}
        />
      ) : (
        <div className="space-y-3">
          <ErrorBanner msg={errors._} />

          {/* Agency details */}
          <div className="grid grid-cols-2 gap-x-3 gap-y-3">
            <div className="col-span-2 space-y-1">
              <div className="flex items-center justify-between">
                <FieldLabel>Agency Name *</FieldLabel>
                <AvailabilityBadge status={nameAvail} />
              </div>
              <Input
                value={agencyName}
                onChange={(e) => { setAgencyName(e.target.value); checkField('agency_name', e.target.value, setNameAvail) }}
                placeholder="e.g. Acme Recruiting"
                className={cn('h-9', nameAvail === 'taken' && 'border-destructive')}
              />
              <FieldError msg={errors.agencyName} />
            </div>

            <div className="space-y-1">
              <FieldLabel>Type *</FieldLabel>
              <Select value={type} onValueChange={(v) => setType(v ?? '')}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {AGENCY_TYPE_OPTIONS.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError msg={errors.type} />
            </div>

            <div className="space-y-1">
              <FieldLabel>Phone *</FieldLabel>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555…" className="h-9" />
              <FieldError msg={errors.phone} />
            </div>

            <div className="col-span-2 space-y-1">
              <div className="flex items-center justify-between">
                <FieldLabel>Contact Email *</FieldLabel>
                <AvailabilityBadge status={emailAvail} />
              </div>
              <Input
                type="email"
                value={contactEmail}
                onChange={(e) => { setContactEmail(e.target.value); checkField('contact_email', e.target.value, setEmailAvail) }}
                placeholder="contact@agency.com"
                className={cn('h-9', emailAvail === 'taken' && 'border-destructive')}
              />
              <FieldError msg={errors.contactEmail} />
            </div>
          </div>

          <SectionDivider label="Admin User" />

          <div className="grid grid-cols-2 gap-x-3 gap-y-3">
            <div className="space-y-1">
              <FieldLabel>Email *</FieldLabel>
              <Input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="admin@agency.com" className="h-9" />
              <FieldError msg={errors.adminEmail} />
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <FieldLabel>Username *</FieldLabel>
                <AvailabilityBadge status={usernameAvail} />
              </div>
              <Input
                value={adminUsername}
                onChange={(e) => { setAdminUsername(e.target.value); checkField('username', e.target.value, setUsernameAvail) }}
                placeholder="admin_user"
                className={cn('h-9', usernameAvail === 'taken' && 'border-destructive')}
              />
              <FieldError msg={errors.adminUsername} />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-2">
            <AdvancedLink path="/custom/create-agency" label="Full agency creation" onNavigate={(p) => { handleClose(); push(p) }} />
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleClose} className="h-8 text-xs">Cancel</Button>
              <Button
                size="sm"
                className="h-8 text-xs bg-[#076698] hover:bg-[#055580] min-w-[110px] gap-1.5"
                onClick={handleSubmit}
                disabled={status === 'submitting'}
              >
                {status === 'submitting'
                  ? <><Loader2 size={12} className="animate-spin" /> Creating…</>
                  : <><Building2 size={12} /> Create Agency</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ModalShell>
  )
}

/* ─── Send Email Quick Modal ──────────────────────────────────── */
export function QuickSendEmailModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [recipient, setRecipient] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState('')
  const { open: notify } = useNotification()
  const { push } = useNavigation()

  const reset = () => { setRecipient(''); setSubject(''); setBody(''); setStatus('idle'); setError('') }
  const handleClose = () => { reset(); onClose() }

  const handleSend = async () => {
    if (!recipient || !subject || !body) {
      notify?.({ type: 'error', message: 'All fields are required' })
      return
    }
    setStatus('submitting')
    try {
      await flaskApi.post('/messaging/email', { recipient_email: recipient, subject, body })
      setStatus('success')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { description?: string } } })?.response?.data?.description ?? 'Failed to send email'
      setError(msg)
      setStatus('error')
    }
  }

  return (
    <ModalShell
      open={open} onClose={handleClose}
      accentBg="bg-gradient-to-r from-amber-50 to-orange-50/60 dark:from-amber-900/20 dark:to-orange-900/10"
      accentText="text-amber-700 dark:text-amber-300"
      icon={<Mail size={18} />}
      title="Send Email"
      subtitle="Queue a one-off email to any recipient"
    >
      {status === 'success' ? (
        <ModalSuccess
          icon={<Mail size={22} className="text-emerald-600" />}
          message={`Email queued for ${recipient}. It will be sent via the platform notification queue.`}
          onReset={reset}
          onClose={handleClose}
          resetLabel="Send Another"
        />
      ) : (
        <div className="space-y-3">
          <ErrorBanner msg={error} />
          <div className="space-y-1">
            <FieldLabel>Recipient *</FieldLabel>
            <Input type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="recipient@example.com" className="h-9" />
          </div>
          <div className="space-y-1">
            <FieldLabel>Subject *</FieldLabel>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject" className="h-9" />
          </div>
          <div className="space-y-1">
            <FieldLabel>Body *</FieldLabel>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              placeholder="Write your message…"
              className="w-full px-3 py-2 text-sm form-field resize-y"
            />
          </div>
          <div className="flex items-center justify-between pt-1">
            <AdvancedLink path="/custom/messaging" label="Full messaging page" onNavigate={(p) => { handleClose(); push(p) }} />
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleClose} className="h-8 text-xs">Cancel</Button>
              <Button
                size="sm"
                className="h-8 text-xs bg-amber-600 hover:bg-amber-700 min-w-[90px] gap-1.5"
                onClick={handleSend}
                disabled={status === 'submitting'}
              >
                {status === 'submitting'
                  ? <><Loader2 size={12} className="animate-spin" /> Sending…</>
                  : <><ArrowRight size={12} /> Send Email</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ModalShell>
  )
}

/* ─── Send WhatsApp Quick Modal ───────────────────────────────── */
interface WaTemplate { name: string; params: string[]; languages: string[] }

export function QuickSendWhatsAppModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [phone, setPhone] = useState('')
  const [templateName, setTemplateName] = useState('')
  const [language, setLanguage] = useState('en')
  const [params, setParams] = useState<Record<string, string>>({})
  const [templates, setTemplates] = useState<WaTemplate[]>([])
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState('')
  const { open: notify } = useNotification()
  const { push } = useNavigation()

  useEffect(() => {
    if (!open || templates.length > 0) return
    setLoadingTemplates(true)
    flaskApi.get<{ items: WaTemplate[] }>('/messaging/whatsapp-templates')
      .then((r) => setTemplates(r.data.items))
      .catch(() => { })
      .finally(() => setLoadingTemplates(false))
  }, [open, templates.length])

  const selectedTemplate = templates.find((t) => t.name === templateName)
  const templateParams = selectedTemplate?.params ?? []
  const availableLanguages = selectedTemplate?.languages ?? ['en', 'ar']

  const reset = () => {
    setPhone(''); setTemplateName(''); setLanguage('en')
    setParams({}); setStatus('idle'); setError('')
  }
  const handleClose = () => { reset(); onClose() }

  const handleTemplateChange = (name: string) => {
    setTemplateName(name)
    setParams({})
    const t = templates.find((x) => x.name === name)
    if (t?.languages?.length) setLanguage(t.languages[0])
  }

  const handleSend = async () => {
    if (!phone || !templateName) {
      notify?.({ type: 'error', message: 'Phone and template are required' })
      return
    }
    setStatus('submitting')
    try {
      await flaskApi.post('/messaging/whatsapp', {
        phone, template_name: templateName, language, parameters: params,
      })
      setStatus('success')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { description?: string } } })?.response?.data?.description ?? 'Failed to queue message'
      setError(msg)
      setStatus('error')
    }
  }

  return (
    <ModalShell
      open={open} onClose={handleClose}
      accentBg="bg-gradient-to-r from-emerald-50 to-teal-50/60 dark:from-emerald-900/20 dark:to-teal-900/10"
      accentText="text-emerald-700 dark:text-emerald-300"
      icon={<MessageCircle size={18} />}
      title="Send WhatsApp"
      subtitle="Template message via notification queue"
    >
      {status === 'success' ? (
        <ModalSuccess
          icon={<MessageCircle size={22} className="text-emerald-600" />}
          message={`WhatsApp queued for ${phone} using the "${templateName}" template.`}
          onReset={reset}
          onClose={handleClose}
          resetLabel="Send Another"
        />
      ) : (
        <div className="space-y-3">
          <ErrorBanner msg={error} />

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <FieldLabel>Phone *</FieldLabel>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1234567890" className="h-9" />
            </div>
            <div className="space-y-1">
              <FieldLabel>Language</FieldLabel>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full h-9 px-2.5 text-sm form-field"
              >
                {availableLanguages.map((l) => (
                  <option key={l} value={l}>{l === 'en' ? 'English' : l === 'ar' ? 'Arabic' : l}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <FieldLabel>Template *</FieldLabel>
            {loadingTemplates ? (
              <div className="flex items-center gap-2 h-9 px-3 rounded-lg bg-slate-50 dark:bg-slate-700/40 border border-input text-xs text-muted-foreground">
                <Loader2 size={12} className="animate-spin" /> Loading templates…
              </div>
            ) : (
              <Select value={templateName} onValueChange={(v) => handleTemplateChange(v ?? '')}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Select a template…" /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {templateParams.length > 0 && (
            <>
              <SectionDivider label={`${templateParams.length} Parameter${templateParams.length > 1 ? 's' : ''}`} />
              <div className="space-y-2">
                {templateParams.map((p) => (
                  <div key={p} className="flex items-center gap-2">
                    <span className="text-[10px] font-mono font-bold text-slate-400 dark:text-slate-500 w-28 shrink-0 truncate">{p}</span>
                    <Input
                      value={params[p] ?? ''}
                      onChange={(e) => setParams((prev) => ({ ...prev, [p]: e.target.value }))}
                      placeholder={`Enter ${p}…`}
                      className="text-xs h-8 flex-1"
                    />
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="flex items-center justify-between pt-1">
            <AdvancedLink path="/custom/messaging" label="Full messaging page" onNavigate={(p) => { handleClose(); push(p) }} />
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleClose} className="h-8 text-xs">Cancel</Button>
              <Button
                size="sm"
                className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 min-w-[110px] gap-1.5"
                onClick={handleSend}
                disabled={status === 'submitting'}
              >
                {status === 'submitting'
                  ? <><Loader2 size={12} className="animate-spin" /> Sending…</>
                  : <><MessageCircle size={12} /> Send WhatsApp</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ModalShell>
  )
}
